import { computed, onMounted, onBeforeUnmount, shallowRef, watch } from 'vue'
import { resolveStickyTop, scrollsWithPage } from '@/utils/sticky-offset'

const APP_BAR_CLASS = 'v-app-bar'
const APP_BAR_SELECTOR = `.${APP_BAR_CLASS}`

// The safe-area spacer App.vue renders on notched devices. It is fixed and
// paints over the status strip, so a bar has to pin below it, not at 0.
const SYSTEM_BAR_SELECTOR = '.v-system-bar'

// Marks the branding band so StickyFilterBar can measure it and stack below.
// Owned here rather than by either component: it is the contract between them,
// and a rename that touched only one side would silently unstack the two.
export const STICKY_BRANDING_BAR_CLASS = 'StickyBrandingBar'

// The app bar slides over ~200ms, which outlasts the scroll event that started
// it, so keep sampling for a short tail rather than reading a single frame.
const SETTLE_FRAMES = 20

// App bars are async route components, so on first paint there may be nothing
// to measure yet. Watch longer at mount to catch one that arrives late.
const MOUNT_FRAMES = 120

// A bar inside a scrolling element pins flush to that box -- see scrollsWithPage.
const ELEMENT_SCROLLPORT_TOP = 0

/**
 * Ancestor boxes of an element, nearest first, in the shape scrollsWithPage
 * expects.
 *
 * @param {HTMLElement} element
 * @returns {Array<{isPageRoot: boolean, overflowX: string, overflowY: string}>}
 */
const readScrollportChain = element => {
  const chain = []

  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(node)
    chain.push({
      isPageRoot: node === document.body || node === document.documentElement,
      overflowX,
      overflowY,
    })
  }

  return chain
}

/**
 * Drives the `top` of a `position: sticky` bar so it parks below the app bar
 * (and below the safe-area system bar on notched devices) in every scroll
 * state, rather than trusting Vuetify's `--v-layout-top`. See resolveStickyTop
 * for why that variable is not usable.
 *
 * Shared by StickyBrandingBar and StickyFilterBar: the branding band pins to
 * the app bar, and the filter bar pins to the band, so the two stack instead of
 * overlapping.
 *
 * Pass `elementRef` when the bar can end up inside a scrolling element (the
 * rich text toolbar does, on the add-details view): page chrome is then not
 * what it has to clear, and the offset drops to 0.
 *
 * @param {string|null} pinnedAboveSelector selector for a sticky bar that pins
 *   directly above this one, or null when this bar clears the app bar itself
 * @param {{elementRef?: import('vue').Ref<HTMLElement|null>}} options the sticky
 *   element itself, when its scrollport is not known to be the page
 * @returns {{ offsetStyle: import('vue').ComputedRef, offset: import('vue').ShallowRef }}
 *   inline style to bind (null while there is nothing to measure) and the raw
 *   offset in px, for callers that also have to reserve the space in JS
 */
export function useStickyTop(pinnedAboveSelector = null, { elementRef = null } = {}) {
  const offset = shallowRef(null)
  // Resampled on mount and on resize rather than per frame: the walk costs a
  // getComputedStyle per ancestor, and the chain only changes when the element
  // remounts or the layout reflows.
  const pageIsScrollport = shallowRef(true)

  let frame = null
  let remainingFrames = 0

  const offsetStyle = computed(() => (offset.value === null ? null : { top: `${offset.value}px` }))

  const refreshScrollport = () => {
    const element = elementRef?.value
    pageIsScrollport.value = !element || scrollsWithPage(readScrollportChain(element))
  }

  const sample = () => {
    if (!pageIsScrollport.value) {
      offset.value = ELEMENT_SCROLLPORT_TOP
      return
    }

    const appBar = document.querySelector(APP_BAR_SELECTOR)
    const systemBar = document.querySelector(SYSTEM_BAR_SELECTOR)
    const pinnedAbove = pinnedAboveSelector ? document.querySelector(pinnedAboveSelector) : null
    const next = resolveStickyTop(
      appBar?.getBoundingClientRect(),
      systemBar?.getBoundingClientRect(),
      pinnedAbove?.getBoundingClientRect(),
    )

    // A moving app bar means the slide is still in flight -- keep the tail alive.
    if (next !== offset.value) {
      offset.value = next
      remainingFrames = SETTLE_FRAMES
    }
  }

  const tick = () => {
    sample()
    frame = remainingFrames-- > 0 ? requestAnimationFrame(tick) : null
  }

  const schedule = (frames = SETTLE_FRAMES) => {
    remainingFrames = Math.max(remainingFrames, frames)
    if (frame === null) frame = requestAnimationFrame(tick)
  }

  // The app bar slides via a `transform` transition that can start well after the
  // scroll event that triggered it, so watch the transition itself rather than
  // relying on the scroll tail alone -- otherwise the offset goes stale mid-slide.
  const onAppBarTransition = (event) => {
    if (event.target?.classList?.contains(APP_BAR_CLASS)) schedule()
  }

  // Wrapped: passing `schedule` straight to addEventListener would feed the Event
  // object in as the frame budget.
  const onScroll = () => schedule()

  // A resize can swap the scrollport itself (the toolbar wraps to a second row,
  // a breakpoint changes which shell is rendered), so re-read the chain here.
  const onResize = () => {
    refreshScrollport()
    schedule()
  }

  if (elementRef) watch(elementRef, onResize)

  onMounted(() => {
    refreshScrollport()
    // Scheduled rather than sampled once: the app bar can mount after this bar on
    // routes that load it asynchronously.
    schedule(MOUNT_FRAMES)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize, { passive: true })
    document.addEventListener('transitionrun', onAppBarTransition, true)
    document.addEventListener('transitionend', onAppBarTransition, true)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onResize)
    document.removeEventListener('transitionrun', onAppBarTransition, true)
    document.removeEventListener('transitionend', onAppBarTransition, true)
    if (frame !== null) cancelAnimationFrame(frame)
  })

  return { offsetStyle, offset }
}
